// ======================================================================
// 📁 File: EquipmentDrawer.jsx
// MODULE: PPE → Engineering → Equipment Master
// ======================================================================

import React, { useEffect, useState } from "react";
import axiosClient from "../../../../../api/axiosClient";

import {
  createEquipment,
  updateEquipment,
} from "../../../pages/engineering/equipment/services/equipmentService";

export default function EquipmentDrawer({
  open,
  mode = "create",
  record = null,
  onClose,
  onSaved,
}) {
  const [form, setForm] = useState({
    equipment_code: "",
    equipment_name: "",
    equipment_category: "",
    product_family_id: "",
    product_variant_id: "",
    description: "",
    is_active: 1,
  });

  const [families, setFamilies] = useState([]);
  const [variants, setVariants] = useState([]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // ============================================================
  // Load Product Families
  // ============================================================
  const loadFamilies = async () => {
    try {
      const res = await axiosClient.get(
        "/api/ppe/masters/product-families"
      );

      setFamilies(res.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  
  // ============================================================
// Load Variants By Family
// ============================================================
const loadVariants = async (familyId) => {
  if (!familyId) {
    setVariants([]);
    return;
  }

  try {
    const res = await axiosClient.get(
      "/api/ppe/masters/product-variants",
      {
        params: {
          product_family_id: familyId,
          is_active: 1,
        },
      }
    );

    const responseData = res.data?.data;

    const list = Array.isArray(responseData)
      ? responseData
      : Array.isArray(responseData?.data)
      ? responseData.data
      : [];

    // Keep only variants belonging to the selected family.
    const filteredVariants = list.filter(
      (variant) =>
        String(variant.product_family_id) === String(familyId) &&
        variant.is_active !== false &&
        Number(variant.is_active ?? 1) === 1
    );

    setVariants(filteredVariants);
  } catch (err) {
    console.error("Failed to load product variants:", err);
    setVariants([]);
  }
};
  // ============================================================
  // Load Variants By Family
  // ============================================================
  
  const loadVariants_old1 = async (familyId) => {
    try {
      if (!familyId) {
        setVariants([]);
        return;
      }

      const res = await axiosClient.get(
        "/api/ppe/masters/product-variants",
        {
          params: {
            product_family_id: familyId,
            is_active: 1,
          },
        }
      );

      setVariants(res.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  // ============================================================
  // Initialize
  // ============================================================
  useEffect(() => {
    loadFamilies();
  }, []);

  useEffect(() => {
    if (mode === "edit" && record) {
      setForm({
        equipment_code:
          record.equipment_code || "",

        equipment_name:
          record.equipment_name || "",

        equipment_category:
          record.equipment_category || "",

        product_family_id:
          record.product_family_id || "",

        product_variant_id:
          record.product_variant_id || "",

        description:
          record.description || "",

        is_active:
          record.is_active ? 1 : 0,
      });

      if (record.product_family_id) {
        loadVariants(
          record.product_family_id
        );
      }
    } else {
      setForm({
        equipment_code: "",
        equipment_name: "",
        equipment_category: "",
        product_family_id: "",
        product_variant_id: "",
        description: "",
        is_active: 1,
      });

      setVariants([]);
    }
  }, [mode, record]);

  if (!open) return null;

  // ============================================================
  // Change Handler
  // ============================================================
  const onChange = (
    field,
    value
  ) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // ============================================================
  // Product Family Changed
  // ============================================================
  const onFamilyChange = async (
    familyId
  ) => {
    setForm((prev) => ({
      ...prev,
      product_family_id: familyId,
      product_variant_id: "",
    }));

    await loadVariants(familyId);
  };

  // ============================================================
  // Submit
  // ============================================================
  const onSubmit = async () => {
    try {
      setError(null);

      if (
        !form.equipment_code ||
        !form.equipment_name
      ) {
        setError(
          "Equipment Code and Equipment Name are mandatory"
        );
        return;
      }

      setSaving(true);

      const payload = {
        equipment_code:
          form.equipment_code,

        equipment_name:
          form.equipment_name,

        equipment_category:
          form.equipment_category,

        product_variant_id:
          form.product_variant_id || null,

        description:
          form.description,

        is_active:
          form.is_active,
      };

      // ------------------------------------------------
      // CREATE
      // ------------------------------------------------
      if (mode === "create") {
        await createEquipment(
          payload
        );
      }

      // ------------------------------------------------
      // EDIT
      // ------------------------------------------------
      if (
        mode === "edit" &&
        record?.id
      ) {
        await updateEquipment(
          record.id,
          payload
        );
      }

      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Save failed"
      );
    } finally {
      setSaving(false);
    }
  };

  // ============================================================
  // Render
  // ============================================================
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        width: 520,
        height: "100vh",
        background: "#fff",
        boxShadow:
          "-4px 0 16px rgba(0,0,0,0.15)",
        zIndex: 2000,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ================================================== */}
      {/* Header */}
      {/* ================================================== */}
      <div
        style={{
          padding: 16,
          borderBottom:
            "1px solid #e5e7eb",
          display: "flex",
          justifyContent:
            "space-between",
          alignItems: "center",
        }}
      >
        <h6 style={{ margin: 0 }}>
          {mode === "create"
            ? "Create Equipment"
            : "Edit Equipment"}
        </h6>

        <button
          className="btn btn-sm btn-light"
          onClick={onClose}
        >
          ✕
        </button>
      </div>

      {/* ================================================== */}
      {/* Body */}
      {/* ================================================== */}
      <div
        style={{
          padding: 16,
          flex: 1,
          overflowY: "auto",
        }}
      >
        {error && (
          <div className="alert alert-danger">
            {error}
          </div>
        )}

        {/* ============================================== */}
        {/* Equipment Information */}
        {/* ============================================== */}
        <div className="card mb-3">
          <div className="card-header">
            Equipment Information
          </div>

          <div className="card-body">

            <div className="mb-3">
              <label className="form-label">
                Equipment Code *
              </label>

              <input
                className="form-control"
                value={
                  form.equipment_code
                }
                onChange={(e) =>
                  onChange(
                    "equipment_code",
                    e.target.value
                  )
                }
              />
            </div>

            <div className="mb-3">
              <label className="form-label">
                Equipment Name *
              </label>

              <input
                className="form-control"
                value={
                  form.equipment_name
                }
                onChange={(e) =>
                  onChange(
                    "equipment_name",
                    e.target.value
                  )
                }
              />
            </div>

            <div className="mb-3">
              <label className="form-label">
                Equipment Category
              </label>

              <input
                className="form-control"
                value={
                  form.equipment_category
                }
                onChange={(e) =>
                  onChange(
                    "equipment_category",
                    e.target.value
                  )
                }
              />
            </div>

          </div>
        </div>

        {/* ============================================== */}
        {/* Engineering Association */}
        {/* ============================================== */}
        <div className="card mb-3">
          <div className="card-header">
            Engineering Association
          </div>

          <div className="card-body">

            <div className="mb-3">
              <label className="form-label">
                Product Family
              </label>

              <select
                className="form-select"
                value={
                  form.product_family_id
                }
                onChange={(e) =>
                  onFamilyChange(
                    e.target.value
                  )
                }
              >
                <option value="">
                  Select Product Family
                </option>

                {families.map((f) => (
                  <option
                    key={f.id}
                    value={f.id}
                  >
                    {f.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label">
                Product Variant
              </label>

              <select
                className="form-select"
                value={
                  form.product_variant_id
                }
                onChange={(e) =>
                  onChange(
                    "product_variant_id",
                    e.target.value
                  )
                }
              >
                <option value="">
                  Select Product Variant
                </option>

                {variants.map((v) => (
                  <option
                    key={v.id}
                    value={v.id}
                  >
                    {v.name}
                  </option>
                ))}
              </select>
            </div>

          </div>
        </div>

        {/* ============================================== */}
        {/* Description */}
        {/* ============================================== */}
        <div className="card">
          <div className="card-header">
            Additional Information
          </div>

          <div className="card-body">

            <div className="mb-3">
              <label className="form-label">
                Description
              </label>

              <textarea
                rows={4}
                className="form-control"
                value={
                  form.description
                }
                onChange={(e) =>
                  onChange(
                    "description",
                    e.target.value
                  )
                }
              />
            </div>

            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                checked={
                  form.is_active === 1
                }
                onChange={(e) =>
                  onChange(
                    "is_active",
                    e.target.checked
                      ? 1
                      : 0
                  )
                }
              />

              <label className="form-check-label">
                Active
              </label>
            </div>

          </div>
        </div>
      </div>

      {/* ================================================== */}
      {/* Footer */}
      {/* ================================================== */}
      <div
        style={{
          padding: 16,
          borderTop:
            "1px solid #e5e7eb",
          display: "flex",
          justifyContent:
            "flex-end",
          gap: 8,
        }}
      >
        <button
          className="btn btn-outline-secondary"
          onClick={onClose}
        >
          Cancel
        </button>

        <button
          className="btn btn-primary"
          disabled={saving}
          onClick={onSubmit}
        >
          {saving
            ? "Saving..."
            : "Save"}
        </button>
      </div>
    </div>
  );
}